import React, { useEffect, useMemo, useState } from 'react';
import { router } from '@inertiajs/react';
import Layout from '../components/Layout.jsx';

// ---------- date helpers ----------
function parseDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}
// JS's `new Date("YYYY-MM-DD")` parses as UTC midnight, which becomes "the
// previous day" for any user east of UTC. Date-only fields (e.g. compliance
// deadline) need to be interpreted as a calendar date in the viewer's local
// timezone — so build the Date from the component parts.
function parseLocalDate(input) {
  if (!input) return null;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return parseDate(input);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function fmtDate(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtLongDate(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtTime(d) {
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function fmtTimeRange(start, end) {
  if (!start) return '';
  return end ? `${fmtTime(start)} – ${fmtTime(end)}` : fmtTime(start);
}
function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function diffDays(target, from = new Date()) {
  if (!target) return null;
  return Math.ceil((target.getTime() - from.setHours(0, 0, 0, 0)) / 86400000);
}

function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  const last  = endOfMonth(anchor);
  const startWeekday = first.getDay();
  const days = [];
  for (let i = 0; i < startWeekday; i++) {
    days.push({ date: new Date(anchor.getFullYear(), anchor.getMonth(), -(startWeekday - 1 - i)), outside: true });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    days.push({ date: new Date(anchor.getFullYear(), anchor.getMonth(), d), outside: false });
  }
  while (days.length % 7 !== 0) {
    const next = new Date(days[days.length - 1].date);
    next.setDate(next.getDate() + 1);
    days.push({ date: next, outside: true });
  }
  return days;
}

// ---------- status classification ----------
function eventStatus(w) {
  if (w.registered) return 'registered';
  if (w.capacity != null && w.seats_remaining === 0) return 'full';
  if (w.capacity != null && w.seats_remaining > 0 && w.seats_remaining <= 5) return 'limited';
  return 'available';
}
const STATUS_DOT = {
  registered: 'bg-indigo-500',
  available:  'bg-emerald-500',
  limited:    'bg-amber-500',
  full:       'bg-gray-400',
};
const STATUS_LABEL = {
  registered: 'Registered',
  available:  'Join Available',
  limited:    'Limited Seats',
  full:       'Full',
};

// ---------- icons (heroicons-style outline) ----------
const I = {
  info: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  ),
  calendar: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 9.75h18M5.25 5.25h13.5A2.25 2.25 0 0121 7.5v11.25A2.25 2.25 0 0118.75 21H5.25A2.25 2.25 0 013 18.75V7.5a2.25 2.25 0 012.25-2.25z" />
    </svg>
  ),
  flag: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18m0-13.5h12.75l-2.25 4.5 2.25 4.5H3" />
    </svg>
  ),
  clock: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  hourglass: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12M6 21h12M7.5 3v3.75A4.5 4.5 0 0010.5 11l1.5 1 1.5-1A4.5 4.5 0 0016.5 6.75V3M7.5 21v-3.75A4.5 4.5 0 0110.5 13l1.5-1 1.5 1a4.5 4.5 0 013 4.25V21" />
    </svg>
  ),
  user: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.118a7.5 7.5 0 0115 0v.132A18 18 0 0112 21.75a18 18 0 01-7.5-1.5z" />
    </svg>
  ),
  check: (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  chevronLeft: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  ),
  chevronRight: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  ),
  cap: (cls = 'w-5 h-5') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.43 60.43 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.9 59.9 0 0112 3.493a59.9 59.9 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.7 50.7 0 0112 13.489a50.7 50.7 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.4 55.4 0 0112 8.443m-7.007 11.55A5.98 5.98 0 006.75 15.75v-1.5" />
    </svg>
  ),
  video: (cls = 'w-4 h-4') => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
    </svg>
  ),
};

// ---------- Progress card ----------
function ProgressCard({ progress, nextRegistration }) {
  if (!progress) return null;
  const required  = progress.hours_required;
  const completed = Number(progress.hours_completed || 0);
  const remaining = progress.hours_remaining;
  const pct = required && required > 0 ? Math.max(0, Math.min(100, (completed / required) * 100)) : 0;

  const subhead = progress.certification_type === 'initial_certification'
    ? 'Initial Certification Requirement'
    : progress.certification_type === 'recertification'
      ? 'Recertification Requirement'
      : 'Live Webinar Progress';

  const nextW = progress.next_webinar;
  const nextStart = parseDate(nextW?.starts_at);
  const deadline = parseLocalDate(progress.compliance_deadline);
  const daysRemaining = diffDays(deadline);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-1.5">
          <h2 className="text-lg font-semibold text-gray-900">Live Webinar Progress</h2>
          <span className="text-gray-400">{I.info('w-4 h-4')}</span>
        </div>
        <div className="flex items-start gap-8">
          {nextW && (
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-indigo-600">
                {I.calendar('w-4 h-4')}
                <p className="text-sm font-semibold">Next Webinar</p>
              </div>
              <p className="text-sm font-semibold text-gray-900 mt-1 truncate">{nextW.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{fmtLongDate(nextStart)} at {fmtTime(nextStart)}</p>
            </div>
          )}
          {deadline && (
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-gray-500">
                {I.flag('w-4 h-4')}
                <p className="text-sm font-semibold">Compliance Deadline</p>
              </div>
              <p className="text-sm font-semibold text-gray-900 mt-1">{fmtLongDate(deadline)}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {daysRemaining > 0 ? `${daysRemaining} days remaining` : daysRemaining === 0 ? 'Due today' : 'Past due'}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <p className="text-sm text-gray-700 mb-2">{subhead}</p>
          {required ? (
            <>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-semibold text-emerald-600">{completed}</span> / {required} Hours Completed
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">Set your certification type in your profile to track required hours.</p>
          )}
        </div>
        <div className="lg:text-right">
          <p className="text-3xl font-bold text-gray-900 tabular-nums">{remaining != null ? remaining : '—'}</p>
          <p className="text-xs font-medium text-gray-500 mt-0.5">Hours Remaining</p>
        </div>
      </div>
    </div>
  );
}

// ---------- Calendar ----------
const MAX_EVENTS_PER_CELL = 3;

function CalendarPanel({ view, setView, anchorMonth, setAnchorMonth, webinars, selectedId, onSelect }) {
  const days = buildMonthGrid(anchorMonth);
  const today = new Date();
  const monthLabel = anchorMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const eventsByDay = useMemo(() => {
    const map = new Map();
    webinars.forEach((w) => {
      const start = parseDate(w.starts_at);
      if (!start) return;
      const key = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(w);
    });
    return map;
  }, [webinars]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-end justify-between gap-3 flex-wrap border-b border-gray-100 -mx-1 px-1 mb-4">
        <div className="flex gap-5 -mb-px">
          {['calendar', 'list'].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setView(k)}
              className={`pb-2.5 text-sm font-semibold border-b-2 transition cursor-pointer ${
                view === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {k === 'calendar' ? 'Calendar View' : 'List View'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 pb-2">
          <button
            type="button"
            onClick={() => setAnchorMonth(new Date(today.getFullYear(), today.getMonth(), 1))}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-200 cursor-pointer"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setAnchorMonth(addMonths(anchorMonth, -1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 cursor-pointer"
            aria-label="Previous month"
          >
            {I.chevronLeft('w-4 h-4')}
          </button>
          <button
            type="button"
            onClick={() => setAnchorMonth(addMonths(anchorMonth, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 cursor-pointer"
            aria-label="Next month"
          >
            {I.chevronRight('w-4 h-4')}
          </button>
          <span className="ml-2 text-sm font-semibold text-gray-900">{monthLabel}</span>
        </div>
      </div>

      {view === 'calendar' ? (
        <CalendarGrid
          days={days}
          eventsByDay={eventsByDay}
          selectedId={selectedId}
          onSelect={onSelect}
          onShowMore={() => setView('list')}
        />
      ) : (
        <ListView webinars={webinars} selectedId={selectedId} onSelect={onSelect} />
      )}

      <div className="flex items-center gap-4 flex-wrap mt-4 pt-4 border-t border-gray-100 text-xs text-gray-600">
        {Object.keys(STATUS_LABEL).map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[k]}`} />
            {STATUS_LABEL[k]}
          </span>
        ))}
      </div>
    </div>
  );
}

function CalendarGrid({ days, eventsByDay, selectedId, onSelect, onShowMore }) {
  const today = new Date();

  return (
    <>
      <div className="grid grid-cols-7 gap-px text-[10px] font-bold tracking-wider text-gray-400 uppercase mb-1">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
          <div key={d} className="px-2 pb-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-lg overflow-hidden border border-gray-100">
        {days.map(({ date, outside }, i) => {
          const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          const events = eventsByDay.get(key) || [];
          const isToday = sameDay(date, today);
          const isSelectedDay = events.some((e) => e.id === selectedId);
          const hiddenCount = Math.max(0, events.length - MAX_EVENTS_PER_CELL);

          return (
            <div
              key={i}
              className={`min-h-[72px] sm:min-h-[88px] p-1.5 flex flex-col gap-1 ${
                outside ? 'bg-gray-50/60' : 'bg-white'
              }`}
            >
              <span
                className={`text-xs font-semibold w-6 h-6 inline-flex items-center justify-center rounded-full ${
                  isSelectedDay
                    ? 'bg-indigo-600 text-white'
                    : isToday
                      ? 'text-indigo-700'
                      : outside ? 'text-gray-300' : 'text-gray-500'
                }`}
              >
                {date.getDate()}
              </span>
              <div className="space-y-1 overflow-hidden">
                {events.slice(0, MAX_EVENTS_PER_CELL).map((e) => (
                  <EventChip key={e.id} event={e} selected={e.id === selectedId} onSelect={onSelect} />
                ))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={onShowMore}
                    className="text-[10px] text-indigo-600 hover:text-indigo-700 font-semibold px-1 cursor-pointer"
                  >
                    +{hiddenCount} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function EventChip({ event, selected, onSelect }) {
  const status = eventStatus(event);
  const start = parseDate(event.starts_at);
  return (
    <button
      type="button"
      onClick={() => onSelect(event.id)}
      title={start ? `${fmtTime(start)} — ${event.title}` : event.title}
      className={`w-full text-left px-1.5 py-1 rounded-md text-[11px] leading-tight cursor-pointer transition border flex items-center gap-1.5 ${
        selected
          ? 'bg-indigo-50 border-indigo-300'
          : 'bg-slate-50 hover:bg-slate-100 border-transparent'
      }`}
    >
      <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
      <span className="min-w-0 text-gray-800 font-semibold truncate">{event.title}</span>
    </button>
  );
}

function ListView({ webinars, selectedId, onSelect }) {
  if (webinars.length === 0) {
    return <p className="text-sm text-gray-500 py-8 text-center">No upcoming webinars.</p>;
  }
  return (
    <ul className="divide-y divide-gray-100">
      {webinars.map((w) => {
        const start = parseDate(w.starts_at);
        const status = eventStatus(w);
        const isSelected = w.id === selectedId;
        return (
          <li key={w.id}>
            <button
              type="button"
              onClick={() => onSelect(w.id)}
              className={`w-full text-left py-3 px-3 rounded-lg flex items-center gap-4 cursor-pointer transition ${
                isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="shrink-0 w-14 h-14 rounded-lg bg-gray-50 border border-gray-200 flex flex-col items-center justify-center">
                <span className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">
                  {start ? start.toLocaleString(undefined, { month: 'short' }) : ''}
                </span>
                <span className="text-lg font-bold leading-none text-gray-900">{start ? start.getDate() : ''}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                  {w.title}
                  {w.visibility === 'private' && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200 align-middle">Private</span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {fmtTime(start)} • {w.credit_hours} CE hour{Number(w.credit_hours) === 1 ? '' : 's'}
                  {w.instructor ? ` • ${w.instructor}` : ''}
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
                {STATUS_LABEL[status]}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---------- Webinar detail panel ----------
function WebinarDetail({ webinar, onRegister, onCancel, submitting }) {
  if (!webinar) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <p className="text-sm text-gray-500">Select a webinar to see details.</p>
      </div>
    );
  }
  const start  = parseDate(webinar.starts_at);
  const end    = parseDate(webinar.ends_at);
  const status = eventStatus(webinar);
  const full   = webinar.capacity != null && webinar.seats_remaining === 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase bg-indigo-50 text-indigo-700 border border-indigo-200">
          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
          {STATUS_LABEL[status]}
        </span>
        {webinar.visibility === 'private' && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase bg-amber-50 text-amber-800 border border-amber-200">
            Private
          </span>
        )}
      </div>
      <h3 className="mt-3 text-xl font-bold text-gray-900">{webinar.title}</h3>

      <dl className="mt-4 space-y-2.5">
        <DetailRow icon={I.calendar()} >{fmtDate(start)}</DetailRow>
        <DetailRow icon={I.clock()}    >{fmtTimeRange(start, end)}</DetailRow>
        <DetailRow icon={I.hourglass()}>{webinar.credit_hours} Credit Hours</DetailRow>
        {webinar.instructor && (
          <DetailRow icon={I.user()}>Instructor: {webinar.instructor}</DetailRow>
        )}
      </dl>

      {webinar.description && (
        <p className="mt-4 text-sm text-gray-600 whitespace-pre-line leading-relaxed">{webinar.description}</p>
      )}

      <div className="mt-5 pt-4 border-t border-gray-100">
        {webinar.registered ? (
          <>
            <div className="flex items-start gap-2 text-emerald-700">
              {I.check('w-5 h-5 shrink-0 mt-0.5')}
              <div>
                <p className="text-sm font-semibold">You are registered</p>
                <p className="text-xs text-gray-500 mt-0.5">We'll send you a reminder prior to the webinar.</p>
              </div>
            </div>
            {webinar.join_window_open && webinar.join_url ? (
              <a
                href={webinar.join_url}
                target={webinar.join_url.startsWith('/') ? '_self' : '_blank'}
                rel="noopener noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm cursor-pointer"
              >
                {I.video('w-4 h-4')} Join Webinar
              </a>
            ) : null}
            <button
              type="button"
              onClick={() => onCancel(webinar.id)}
              disabled={submitting}
              className="mt-2 w-full px-4 py-2.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
            >
              Cancel Registration
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500">
              {webinar.capacity != null
                ? `${webinar.seats_remaining} of ${webinar.capacity} seats remaining`
                : 'Open registration'}
            </p>
            <button
              type="button"
              onClick={() => onRegister(webinar.id)}
              disabled={submitting || full}
              className="mt-3 w-full px-4 py-2.5 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {full ? 'Full' : 'Register'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({ icon, children }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-gray-700">
      <span className="text-gray-400 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

// ---------- My upcoming sidebar ----------
function MyUpcomingWebinars({ registrations, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const visibleRegistrations = expanded ? registrations : registrations.slice(0, 3);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">My Upcoming Webinars</h3>
        {registrations.length > 3 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
          >
            {expanded ? 'Show Less' : 'View All'}
          </button>
        )}
      </div>
      {registrations.length === 0 ? (
        <p className="text-sm text-gray-500">You're not registered for any upcoming webinars yet.</p>
      ) : (
        <ul className="space-y-1">
          {visibleRegistrations.map((r) => {
            const start = parseDate(r.starts_at);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onSelect(r.webinar_id)}
                  className="w-full flex items-start gap-3 rounded-xl p-2 -mx-2 hover:bg-gray-50 transition cursor-pointer text-left"
                >
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-gray-50 border border-gray-200 flex flex-col items-center justify-center">
                    <span className="text-[9px] font-bold tracking-wider text-gray-500 uppercase">
                      {start ? start.toLocaleString(undefined, { month: 'short' }) : ''}
                    </span>
                    <span className="text-base font-bold leading-none text-gray-900">{start ? start.getDate() : ''}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {fmtTime(start)} • {r.credit_hours} CE Hour{Number(r.credit_hours) === 1 ? '' : 's'}
                    </p>
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                      Registered
                    </span>
                  </div>
                  <span className="text-gray-300 self-center">{I.chevronRight('w-4 h-4')}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------- Join Webinar countdown ----------
function JoinWebinarCard({ nextRegistration, now }) {
  if (!nextRegistration) return null;
  const start = parseDate(nextRegistration.starts_at);
  const end   = parseDate(nextRegistration.ends_at);
  if (!start) return null;

  const totalSeconds = Math.max(0, Math.floor((start.getTime() - now.getTime()) / 1000));
  const days  = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const mins  = Math.floor((totalSeconds % 3600) / 60);
  const secs  = totalSeconds % 60;

  const isLive   = start && now >= start && (!end || now <= end);
  const joinOpen = nextRegistration.join_window_open
    || (now >= new Date(start.getTime() - 30 * 60000) && (!end || now <= end));
  const startsInMinutes = Math.round((start.getTime() - now.getTime()) / 60000);
  const startsInLabel = isLive
    ? 'Live now'
    : startsInMinutes <= 60 && startsInMinutes > 0
      ? `Starts in ${startsInMinutes} minute${startsInMinutes === 1 ? '' : 's'}`
      : `Starts ${start.toLocaleDateString(undefined, { weekday: 'long' })}`;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900 mb-3">Join Webinar</h3>
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-gray-900 truncate">{nextRegistration.title}</p>
        <p className="text-xs text-indigo-700 font-medium mt-1">{startsInLabel}</p>
        <p className="text-xs text-gray-500 mt-0.5">{fmtDate(start)} • {fmtTime(start)}</p>

        <div className="mt-4 grid grid-cols-4 gap-1.5">
          <CountdownDigit value={days}  label="Days" />
          <CountdownDigit value={hours} label="Hrs" />
          <CountdownDigit value={mins}  label="Mins" />
          <CountdownDigit value={secs}  label="Secs" />
        </div>

        {joinOpen && nextRegistration.join_url ? (
          <a
            href={nextRegistration.join_url}
            target={nextRegistration.join_url.startsWith('/') ? '_self' : '_blank'}
            rel="noopener noreferrer"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-sm"
          >
            {I.video('w-4 h-4')} Join Webinar
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-400 text-sm font-semibold cursor-not-allowed"
          >
            {I.video('w-4 h-4')} Join Webinar
          </button>
        )}
        <p className="mt-2 text-[11px] text-gray-500 text-center">Join is available 30 minutes before start time.</p>
      </div>
    </div>
  );
}

function CountdownDigit({ value, label }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 py-2 text-center">
      <p className="text-lg font-bold text-gray-900 tabular-nums leading-none">{String(value).padStart(2, '0')}</p>
      <p className="text-[9px] font-bold tracking-wider text-gray-500 mt-1 uppercase">{label}</p>
    </div>
  );
}

// ---------- Need to Reschedule ----------
function RescheduleCard() {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          {I.calendar('w-5 h-5')}
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Need to Reschedule?</h3>
          <p className="text-xs text-gray-500 mt-0.5">Can't make it to a webinar?</p>
          <a href="#calendar" className="mt-1 inline-block text-xs font-semibold text-indigo-600 hover:text-indigo-700">
            View other available sessions
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------- Requirements footer ----------
function RequirementsCard({ progress }) {
  if (!progress) return null;
  const required  = progress.hours_required;
  const completed = Number(progress.hours_completed || 0);
  const remaining = progress.hours_remaining;
  const deadline  = parseLocalDate(progress.compliance_deadline);
  const subhead = progress.certification_type === 'initial_certification' ? 'Initial Certification' : 'Recertification';

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          {I.cap('w-6 h-6')}
        </div>
        <h3 className="text-base font-semibold text-gray-900 mr-2">Live Webinar Requirements</h3>
        <div className="flex flex-1 items-center gap-6 flex-wrap min-w-0">
          <RequirementStat label="Required Hours" value={required != null ? `${Number(required).toFixed(1)}` : '—'} sub={subhead} />
          <RequirementStat label="Completed Hours" value={completed.toFixed(1)} valueClass="text-emerald-600" />
          <RequirementStat label="Remaining Hours" value={remaining != null ? Number(remaining).toFixed(1) : '—'} valueClass="text-amber-600" />
          <RequirementStat label="Deadline" value={deadline ? fmtLongDate(deadline) : '—'} />
        </div>
      </div>
    </div>
  );
}

function RequirementStat({ label, value, sub, valueClass = 'text-gray-900' }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${valueClass}`}>{value} <span className="text-xs font-medium text-gray-500">{sub}</span></p>
    </div>
  );
}

// ---------- Page ----------
export default function Webinars({ webinars = [], my_registrations = [], progress = null }) {
  const initialAnchor = useMemo(() => {
    const first = parseDate(webinars[0]?.starts_at) || new Date();
    return new Date(first.getFullYear(), first.getMonth(), 1);
  }, [webinars]);

  const [anchorMonth, setAnchorMonth] = useState(initialAnchor);
  const [selectedId, setSelectedId]   = useState(my_registrations[0]?.webinar_id || webinars[0]?.id || null);
  const [view, setView]               = useState('calendar');
  const [submitting, setSubmitting]   = useState(false);
  const [now, setNow]                 = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const selected         = webinars.find((w) => w.id === selectedId) || null;
  const nextRegistration = my_registrations[0] || null;

  const onRegister = (id) => {
    setSubmitting(true);
    router.post(`/webinars/${id}/register`, {}, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    });
  };
  const onCancel = (id) => {
    setSubmitting(true);
    router.delete(`/webinars/${id}/register`, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    });
  };

  return (
    <Layout>
      <div className="w-full px-6 sm:px-8 py-6 sm:py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Live Webinars</h1>
          <p className="mt-1 text-sm text-gray-500">
            Join live webinars to earn credit hours toward your administrator requirement.
          </p>
        </div>

        <ProgressCard progress={progress} nextRegistration={nextRegistration} />

        <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
          <div className="space-y-6 min-w-0" id="calendar">
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
              <CalendarPanel
                view={view}
                setView={setView}
                anchorMonth={anchorMonth}
                setAnchorMonth={setAnchorMonth}
                webinars={webinars}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
              <WebinarDetail
                webinar={selected}
                onRegister={onRegister}
                onCancel={onCancel}
                submitting={submitting}
              />
            </div>
            <RequirementsCard progress={progress} />
          </div>
          <aside className="space-y-6 min-w-0">
            <MyUpcomingWebinars registrations={my_registrations} onSelect={setSelectedId} />
            <JoinWebinarCard nextRegistration={nextRegistration} now={now} />
            <RescheduleCard />
          </aside>
        </div>
      </div>
    </Layout>
  );
}
